import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  errorMessage,
  useCapacity,
  useCreateWorkspace,
  useEnvironments,
} from "../api/queries";
import type { Capacity, EnvironmentOption } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { SshKeyForm } from "../components/account/SshKeyForm";
import { PageHeader } from "../components/layout/PageHeader";
import { ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import {
  FALLBACK_MAX_WORKSPACE_HOURS,
  FALLBACK_WORKSPACE_HOURS,
} from "../config";
import { familyIsUnknown, familyLabel, gpuChoices } from "../lib/capacity";
import { pluralize } from "../lib/format";
import { hasKeyFor } from "../lib/keyScopes";

/** Durations offered as one-click presets. */
const HOUR_PRESETS: readonly number[] = [1, 2, 4, 8, 24];

interface FormProps {
  capacity: Capacity;
  environments: EnvironmentOption[];
  /** The cluster-wide emergency stop, published by the server. */
  paused: boolean;
  /**
   * Whether this console also sends its warnings as Slack messages.
   *
   * The form promises where the two clocks announce themselves before
   * anybody agrees to them, so the promise has to be the deployment's own
   * (`me.slack_notifications`) and not this bundle's hope.
   */
  slackNotifications: boolean;
}

function WorkspaceForm({
  capacity,
  environments,
  paused,
  slackNotifications,
}: FormProps): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateWorkspace();

  // Which GPU, before how many (ADR-0020): the family decides which counts
  // are offerable at all. With one family — most deployments — no choice is
  // shown and the form reads exactly as it always did.
  const families = capacity.families;
  const [family, setFamily] = useState<string | null>(null);
  const selectedFamily =
    family ?? capacity.defaults.gpu_family ?? families[0]?.family ?? null;

  // What the request carries, and what the counts below are scoped by — one
  // rule for both, so the form can never offer one GPU and ask for another.
  // An explicit click always counts, even if a later reading of the fleet
  // fails to describe a pool and momentarily publishes one family: dropping
  // the choice there would silently start the GPU that was not picked.
  // Without a click, only a fleet that showed the question sends a family, so
  // a one-family deployment submits exactly what it always did.
  const requestedFamily = family ?? (families.length > 1 ? selectedFamily : null);
  const unreadableFamily = familyIsUnknown(capacity, requestedFamily);

  // One question only: is a GPU free right now, or does a machine have to
  // start first. There is no personal allowance to check — taking GPUs is the
  // point, and giving them back is just as easy.
  const choices = gpuChoices(capacity, requestedFamily);
  const defaultGpus =
    choices.find((choice) => choice.immediate)?.count ??
    choices.find((choice) => !choice.disabled)?.count ??
    1;
  const defaultHours =
    capacity.defaults?.workspace_hours ?? FALLBACK_WORKSPACE_HOURS;
  const maxHours =
    capacity.defaults?.max_workspace_hours ?? FALLBACK_MAX_WORKSPACE_HOURS;
  // What one GPU comes with, in the deployment's own figures. A server that
  // published none says nothing here: a number invented by this bundle is
  // exactly the wrong answer to "how much memory do I get".
  const perGpu = capacity.defaults?.per_gpu ?? null;

  const [gpus, setGpus] = useState<number | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [custom, setCustom] = useState(false);
  const [image, setImage] = useState<string | null>(null);

  const selectedGpus = gpus ?? defaultGpus;
  const selectedHours = hours ?? defaultHours;
  const selectedImage = image ?? environments[0]?.id ?? "";
  const selectedChoice = choices.find((choice) => choice.count === selectedGpus);
  // The server's default duration is what an untouched form submits, so it
  // must be one of the pills — and visibly selected. A form whose pills are
  // all blank while it silently requests 12 hours is a deadline the
  // researcher never agreed to.
  const presets = [...new Set([...HOUR_PRESETS, defaultHours])]
    .filter((preset) => preset <= maxHours)
    .sort((a, b) => a - b);

  const submit = (): void => {
    create.mutate(
      {
        gpus: selectedGpus,
        hours: selectedHours,
        image: selectedImage,
        ...(requestedFamily ? { gpu_family: requestedFamily } : {}),
      },
      { onSuccess: (workspace) => navigate(`/workspaces/${workspace.id}`) },
    );
  };

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {families.length > 1 ? (
        <fieldset className="form__block">
          <legend className="form__legend">Which GPU?</legend>
          <div className="gpu-choices">
            {families.map((entry) => {
              const biggest = Math.max(0, ...entry.allowed_gpu_counts);
              return (
                <label
                  key={entry.family}
                  className="gpu-choice"
                  data-selected={selectedFamily === entry.family}
                >
                  <input
                    type="radio"
                    name="gpu-family"
                    className="sr-only"
                    value={entry.family}
                    checked={selectedFamily === entry.family}
                    aria-label={familyLabel(entry.family)}
                    aria-describedby={`gpu-family-${entry.family}`}
                    onChange={() => {
                      setFamily(entry.family);
                      // A count picked for one family may not exist on the
                      // other; back to that family's own default.
                      setGpus(null);
                    }}
                  />
                  <span
                    className="gpu-choice__count gpu-choice__count--word"
                    aria-hidden="true"
                  >
                    {familyLabel(entry.family)}
                  </span>
                  <span
                    className="gpu-choice__reason"
                    id={`gpu-family-${entry.family}`}
                  >
                    {biggest > 0
                      ? `Up to ${pluralize(biggest, "GPU")} on one machine — ${entry.gpu_free} free right now.`
                      : `${entry.gpu_free} free right now.`}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <fieldset className="form__block">
        <legend className="form__legend">How many GPUs?</legend>
        {unreadableFamily && requestedFamily ? (
          <p className="form__error" role="status">
            The console cannot say what {familyLabel(requestedFamily)} machines
            hold right now, so it offers no size for them. It retries by itself
            — try again in a minute.
          </p>
        ) : null}
        <div className="gpu-choices">
          {choices.map((choice) => (
            <label
              key={choice.count}
              className="gpu-choice"
              data-selected={selectedGpus === choice.count}
              data-disabled={choice.disabled}
            >
              <input
                type="radio"
                name="gpus"
                className="sr-only"
                value={choice.count}
                checked={selectedGpus === choice.count}
                disabled={choice.disabled}
                aria-label={pluralize(choice.count, "GPU")}
                aria-describedby={`gpu-choice-${choice.count}`}
                onChange={() => setGpus(choice.count)}
              />
              <span className="gpu-choice__count" aria-hidden="true">
                {choice.count}
                <span className="gpu-choice__unit">
                  {choice.count === 1 ? "GPU" : "GPUs"}
                </span>
              </span>
              <span className="gpu-choice__reason" id={`gpu-choice-${choice.count}`}>
                {choice.reason}
              </span>
            </label>
          ))}
        </div>
        {selectedChoice && !selectedChoice.immediate && !selectedChoice.disabled ? (
          <p className="form__note">
            You can ask anyway: the console starts a machine and your workspace
            begins on its own when it is up.
          </p>
        ) : null}
        {perGpu ? (
          <p className="form__note">
            Each GPU comes with {pluralize(perGpu.cores, "core")},{" "}
            {perGpu.memory_gb} GiB of memory and {perGpu.scratch_gb} GiB of
            /scratch. Twice the GPUs is twice all three — asking for more GPUs
            is how you ask for more memory.
            {selectedGpus > 1
              ? ` Your ${selectedGpus} GPUs come with ${selectedGpus * perGpu.cores} cores and ${selectedGpus * perGpu.memory_gb} GiB of memory.`
              : ""}
          </p>
        ) : null}
        <p className="form__note">
          Not sure how many? Take what you need now — you can change the count
          later in one click, in either direction.
        </p>
      </fieldset>

      <fieldset className="form__block">
        <legend className="form__legend">For how long?</legend>
        <div className="choice-row">
          {presets.map((preset) => (
            <label
              key={preset}
              className="choice"
              data-selected={!custom && selectedHours === preset}
            >
              <input
                type="radio"
                name="hours"
                className="sr-only"
                value={preset}
                checked={!custom && selectedHours === preset}
                onChange={() => {
                  setCustom(false);
                  setHours(preset);
                }}
              />
              {preset} h
            </label>
          ))}
          <label className="choice" data-selected={custom}>
            <input
              type="radio"
              name="hours"
              className="sr-only"
              checked={custom}
              onChange={() => setCustom(true)}
            />
            Custom
          </label>
        </div>
        {custom ? (
          <p className="form__custom">
            <label htmlFor="custom-hours">Hours</label>
            <input
              id="custom-hours"
              className="input input--narrow"
              type="number"
              min={1}
              max={maxHours}
              value={selectedHours}
              onChange={(event) => setHours(Number(event.target.value))}
            />
            <span className="field__hint">
              1 to {maxHours} hours to start with — not a maximum, just the most
              you can ask for in one go.
            </span>
          </p>
        ) : null}
        <p className="form__note">
          You can always add more time later, in one click and with no ceiling —
          and give time back the same way if you finish early. Two things stop a
          workspace: this deadline, and leaving its GPUs unused for{" "}
          {capacity.defaults.idle_threshold_minutes} min. Both are announced on
          the workspace screen, with a countdown, before anything happens
          {slackNotifications
            ? " — and sent to you on Slack, so you do not have to keep the tab open."
            : "."}
        </p>
      </fieldset>

      <fieldset className="form__block">
        <legend className="form__legend">Which environment?</legend>
        {environments.length === 0 ? (
          <p className="form__note">
            The server published no environment. Set IMAGES_FILE on the server
            so there is something to choose from.
          </p>
        ) : (
          <ul className="image-choices">
            {environments.map((option) => (
              <li key={option.id}>
                <label
                  className="image-choice"
                  data-selected={selectedImage === option.id}
                >
                  <input
                    type="radio"
                    name="image"
                    className="sr-only"
                    value={option.id}
                    checked={selectedImage === option.id}
                    aria-label={option.name}
                    aria-describedby={`image-${option.id}`}
                    onChange={() => setImage(option.id)}
                  />
                  <span className="image-choice__name">{option.name}</span>
                  <span
                    className="image-choice__description"
                    id={`image-${option.id}`}
                  >
                    {option.description ?? ""}
                    {option.added_by ? (
                      <span className="image-choice__owner">
                        {option.description ? " — " : ""}added by{" "}
                        {option.added_by}
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="form__note">
          Missing something? <Link to="/environments">Save your own
          environment</Link> — a name of your choice over a container image.
        </p>
      </fieldset>

      {paused ? (
        <p className="form__error" role="status">
          The console is paused right now — no workspace can start. Work
          already running is untouched.
        </p>
      ) : null}

      {create.isError ? (
        <p className="form__error" role="alert">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn btn--primary btn--hero"
        disabled={
          create.isPending ||
          paused ||
          environments.length === 0 ||
          // No matching choice means no count the fleet says it can hold —
          // submitting one anyway would be asking for a size nothing offered.
          (selectedChoice?.disabled ?? true)
        }
      >
        {create.isPending ? "Starting…" : "Start workspace"}
      </button>
    </form>
  );
}

/**
 * Get GPUs: three choices and one button.
 *
 * Returns:
 *     The request screen, or the SSH key step when the account has no key.
 */
export function NewWorkspacePage(): JSX.Element {
  const capacity = useCapacity();
  const environments = useEnvironments();
  const { me } = useSession();

  const blocking = isClusterProblem(capacity.error) ? capacity.error : null;
  const header = (
    <PageHeader
      eyebrow="New workspace"
      title="Get GPUs"
      subtitle="A few choices, then you have a machine."
      back={{ to: "/", label: "GPUs" }}
    />
  );

  if (blocking) {
    return (
      <>
        {header}
        <ClusterProblem error={blocking} onRetry={() => void capacity.refetch()} />
      </>
    );
  }

  if (capacity.isPending || environments.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading what is available…" />
      </>
    );
  }

  if (capacity.isError) {
    return (
      <>
        {header}
        <ErrorBlock error={capacity.error} onRetry={() => void capacity.refetch()} />
      </>
    );
  }

  // The question is not "do you have a key" but "do you have one for your
  // workspaces": somebody whose only key is scoped to training elsewhere has
  // keys on file and still nothing that would let them in. Asked here so they
  // read it before starting, rather than as a refusal afterwards.
  if (!hasKeyFor(me.ssh_public_keys, "workspaces")) {
    const onlyElsewhere = me.ssh_public_keys.length > 0;
    return (
      <>
        {header}
        {onlyElsewhere ? (
          <p className="form__note">
            {me.ssh_public_keys.length === 1
              ? "The key on your account is only for training elsewhere, so it would not let you into this workspace. Add one for your workspaces below, or widen that one on "
              : `The ${pluralize(me.ssh_public_keys.length, "key")} on your account are only for training elsewhere, so none of them would let you into this workspace. Add one for your workspaces below, or widen one of them on `}
            <Link to="/account">your account</Link>.
          </p>
        ) : null}
        <SshKeyForm
          existingKeys={me.ssh_public_keys}
          {...(onlyElsewhere ? { title: "A key for your workspaces" } : {})}
        />
      </>
    );
  }

  if (environments.isError) {
    const problem = environments.error;
    return (
      <>
        {header}
        {isClusterProblem(problem) ? (
          <ClusterProblem
            error={problem}
            onRetry={() => void environments.refetch()}
          />
        ) : (
          <ErrorBlock
            error={problem}
            onRetry={() => void environments.refetch()}
          />
        )}
      </>
    );
  }

  return (
    <>
      {header}
      <WorkspaceForm
        capacity={capacity.data}
        environments={environments.data.items}
        paused={me.console_paused}
        slackNotifications={me.slack_notifications}
      />
    </>
  );
}
